import { z } from "zod";
import { SUPPORTED_CURRENCIES } from "./money.js";
import { ORDER_STATUSES, PAYMENT_STATUSES, PLATFORM_STATUSES, POST_CATEGORIES, POST_STATUSES, REFUND_STATUSES, } from "./enums.js";
/* ── primitives ─────────────────────────────────────────────── */
export const currencySchema = z.enum(SUPPORTED_CURRENCIES);
export const emailSchema = z.string().trim().toLowerCase().email("Invalid email address");
export const passwordSchema = z
    .string()
    .min(10, "Password must be at least 10 characters")
    .max(128, "Password is too long")
    .regex(/[A-Z]/, "Password must contain an uppercase letter")
    .regex(/[a-z]/, "Password must contain a lowercase letter")
    .regex(/[0-9]/, "Password must contain a number");
/** Amount in minor units — positive integer. */
export const amountMinorSchema = z
    .number()
    .int("Amount must be an integer in minor units")
    .positive("Amount must be greater than zero")
    .max(1_000_000_000_00, "Amount exceeds maximum");
export const slugSchema = z
    .string()
    .trim()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens");
export const urlSchema = z.string().trim().url("Must be a valid URL");
/**
 * A platform's public domain, e.g. `numbaport.com` or `https://numbaport.com`.
 *
 * This is display metadata only — it is never fetched or used to build a
 * request — so requiring a scheme (as `urlSchema` does) rejected the natural
 * way to type a domain. Accept a bare hostname, optionally with a scheme, a
 * port and a path, and reject anything that is not a plausible hostname.
 */
export const domainSchema = z
    .string()
    .trim()
    .max(255, "Domain is too long")
    .transform((value) => value.replace(/^https?:\/\//i, "").replace(/\/+$/, ""))
    .refine((value) => /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}(?::\d{1,5})?(?:\/\S*)?$/i.test(value), "Enter a domain like numbaport.com (no https:// needed)");
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
/* ── auth ───────────────────────────────────────────────────── */
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, "Password is required"),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
/* ── platform registry ──────────────────────────────────────── */
export const createPlatformSchema = z.object({
    name: z.string().trim().min(2).max(120),
    slug: slugSchema,
    description: z.string().trim().max(500).optional(),
    // A bare hostname (`numbaport.com`) is the natural thing to type here, so
    // `domain` uses the lenient `domainSchema`. `callbackUrl` stays a strict URL
    // because the worker actually POSTs to it.
    domain: domainSchema.optional().or(z.literal("")),
    callbackUrl: urlSchema.optional().or(z.literal("")),
    status: z.enum(PLATFORM_STATUSES).default("ACTIVE"),
    // Operator-controlled visibility in the public homepage directory. Platforms
    // are listed by default; turning this off hides them from the marketing site
    // without suspending their API access.
    showOnHomepage: z.boolean().default(true),
    feePercent: z.number().min(0).max(100).default(0),
    settings: z.record(z.unknown()).optional(),
});
export const updatePlatformSchema = createPlatformSchema.partial();
export const platformSettingSchema = z.object({
    key: z.string().trim().min(1).max(80),
    value: z.string().max(2000),
});
/* ── orders & payments ──────────────────────────────────────── */
export const createOrderSchema = z.object({
    externalId: z.string().trim().min(1).max(120),
    amountMinor: amountMinorSchema,
    currency: currencySchema,
    description: z.string().trim().max(500).optional(),
    customer: z
        .object({
        externalId: z.string().trim().min(1).max(120),
        email: emailSchema,
        name: z.string().trim().max(120).optional(),
    })
        .optional(),
    metadata: z.record(z.unknown()).optional(),
});
export const initializePaymentSchema = z.object({
    orderReference: z.string().trim().min(1).max(120),
    amountMinor: amountMinorSchema,
    currency: currencySchema,
    customerEmail: emailSchema,
    customerName: z.string().trim().max(120).optional(),
    callbackUrl: urlSchema.optional(),
    metadata: z.record(z.unknown()).optional(),
});
export const refundSchema = z.object({
    amountMinor: amountMinorSchema.optional(),
    reason: z.string().trim().min(3).max(500),
});
/* ── admin users & roles ────────────────────────────────────── */
export const createUserSchema = z.object({
    name: z.string().trim().min(2).max(120),
    email: emailSchema,
    password: passwordSchema,
    roleSlugs: z.array(z.string()).min(1, "At least one role is required"),
    status: z.enum(["ACTIVE", "SUSPENDED"]).default("ACTIVE"),
});
export const updateUserSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    status: z.enum(["ACTIVE", "SUSPENDED"]).optional(),
    roleSlugs: z.array(z.string()).min(1).optional(),
});
export const createRoleSchema = z.object({
    name: z.string().trim().min(2).max(80),
    slug: z
        .string()
        .trim()
        .min(2)
        .max(40)
        .regex(/^[A-Z_]+$/, "Use uppercase letters and underscores"),
    description: z.string().trim().max(300).optional(),
    permissions: z.array(z.string()).default([]),
});
/* ── settings ───────────────────────────────────────────────── */
export const updateSettingsSchema = z.object({
    settings: z.array(z.object({
        key: z.string().trim().min(1).max(80),
        // 4000 was too tight for the live-chat embed: a Tawk.to snippet with the
        // property/widget IDs and the inline config is routinely longer than that,
        // so saving it failed validation and the widget never reached the site.
        value: z.string().max(20000),
        isSecret: z.boolean().optional(),
    })),
});
/* ── content / blog posts ───────────────────────────────────── */
/**
 * Slug for a post URL. Looser than `slugSchema` (which caps at 60 chars) so a
 * descriptive title still produces a readable URL, but still URL-safe.
 */
export const postSlugSchema = z
    .string()
    .trim()
    .min(2, "Slug is too short")
    .max(160, "Slug is too long")
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens");
export const createPostSchema = z.object({
    title: z.string().trim().min(3, "Title is too short").max(200),
    slug: postSlugSchema.optional(),
    excerpt: z.string().trim().max(400).optional().or(z.literal("")),
    body: z.string().trim().min(1, "Write something before saving").max(50000),
    category: z.enum(POST_CATEGORIES).default("UPDATE"),
    status: z.enum(POST_STATUSES).default("DRAFT"),
    pinned: z.boolean().default(false),
});
export const updatePostSchema = createPostSchema.partial();
export const postQuerySchema = paginationSchema.extend({
    status: z.enum(POST_STATUSES).optional(),
    category: z.enum(POST_CATEGORIES).optional(),
    search: z.string().trim().max(120).optional(),
});
/* ── contact ────────────────────────────────────────────────── */
export const contactSchema = z.object({
    name: z.string().trim().min(2).max(120),
    email: emailSchema,
    company: z.string().trim().max(120).optional(),
    subject: z.string().trim().min(3).max(160),
    message: z.string().trim().min(10).max(2000),
});
/* ── query filters ──────────────────────────────────────────── */
/**
 * A date filter coming from the admin UI.
 *
 * `<input type="date">` submits a bare `YYYY-MM-DD`, while API clients send a
 * full ISO-8601 timestamp. Requiring `.datetime()` rejected every filter the
 * admin console sent, which surfaced as "Validation failed" on the
 * transactions, revenue and audit screens. Accept both forms.
 */
const dateFilterSchema = z
    .string()
    .refine((value) => /^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isNaN(Date.parse(value)), "Enter a date as YYYY-MM-DD or a full ISO-8601 timestamp");
export const transactionQuerySchema = paginationSchema.extend({
    platformId: z.string().optional(),
    status: z.enum(PAYMENT_STATUSES).optional(),
    currency: currencySchema.optional(),
    search: z.string().trim().max(120).optional(),
    from: dateFilterSchema.optional(),
    to: dateFilterSchema.optional(),
});
/**
 * Filters for the accounting export.
 *
 * Deliberately shares the same filter fields as `transactionQuerySchema` so an
 * operator exports exactly the rows they are looking at, but without
 * pagination — an export must cover the whole result set, not one page.
 */
export const transactionExportQuerySchema = z.object({
    format: z.enum(["csv", "pdf"]).default("csv"),
    platformId: z.string().optional(),
    status: z.enum(PAYMENT_STATUSES).optional(),
    currency: currencySchema.optional(),
    search: z.string().trim().max(120).optional(),
    from: dateFilterSchema.optional(),
    to: dateFilterSchema.optional(),
});
export const orderQuerySchema = paginationSchema.extend({
    platformId: z.string().optional(),
    status: z.enum(ORDER_STATUSES).optional(),
    search: z.string().trim().max(120).optional(),
});
export const refundQuerySchema = paginationSchema.extend({
    status: z.enum(REFUND_STATUSES).optional(),
    platformId: z.string().optional(),
});
export const auditQuerySchema = paginationSchema.extend({
    action: z.string().trim().max(80).optional(),
    actorType: z.enum(["USER", "PLATFORM", "SYSTEM"]).optional(),
    actorId: z.string().optional(),
    from: dateFilterSchema.optional(),
    to: dateFilterSchema.optional(),
});
export const revenueQuerySchema = z.object({
    from: dateFilterSchema.optional(),
    to: dateFilterSchema.optional(),
    groupBy: z.enum(["day", "month", "platform"]).default("month"),
});
//# sourceMappingURL=schemas.js.map